import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import {
  FinanceService,
  ProjectAutocompleteSuggestions,
  ProjectDetailsPage,
  ProjectDetailsQuery,
  ProjectDetailsRow,
  ReferenceEntry,
} from '../../finance.service';
import { ToasterService } from '@app/shared/services/toaster.service';
import { AuthService } from '@app/core/auth.service';
import {
  AddProjectsModalComponent,
  AddProjectsModalData,
} from './add-projects-modal/add-projects-modal.component';

const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/**
 * Finance Details / Project Details listing page (route
 * /finance/details/projects). Reached via the View Projects button on
 * Finance Details. View-only first cut — Edit / Add Projects / Download
 * actions are stubs that surface a "coming soon" toast until their own
 * specs land.
 */
@Component({
  selector: 'app-finance-projects',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './finance-projects.component.html',
  styleUrl: './finance-projects.component.scss',
})
export class FinanceProjectsComponent implements OnInit, OnDestroy {
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  /** Figma column order — Edit | Status | Business Segment | Supporting LOB |
   *  Product Suite | Agile Team | Project Name | Project ID | SVP | VP |
   *  Functional Manager. */
  readonly displayedColumns: string[] = [
    'edit',
    'status',
    'businessSegment',
    'supportingLob',
    'productSuite',
    'agileTeam',
    'projectName',
    'projectIdCode',
    'svp',
    'vp',
    'functionalManager',
  ];

  rows: ProjectDetailsRow[] = [];
  totalElements = 0;
  totalPages = 1;
  page = 0;
  size = DEFAULT_PAGE_SIZE;

  searchText = '';
  selectedBusinessSegments: string[] = [];
  selectedSupportingLobs: string[] = [];

  businessSegmentOptions: ReferenceEntry[] = [];
  supportingLobOptions: ReferenceEntry[] = [];
  /** Distinct existing values for the 6 free-text autocomplete fields in
   *  the Add / Edit modal. Loaded once on page init so the modal opens
   *  instantly. */
  autocompleteSuggestions: ProjectAutocompleteSuggestions = {
    productSuites: [],
    agileTeams: [],
    svps: [],
    vps: [],
    functionalManagers: [],
    invoiceApprovers: [],
  };

  loading = false;
  errorMessage: string | null = null;

  private readonly searchInput$ = new Subject<void>();
  private readonly subs = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly financeService: FinanceService,
    private readonly toaster: ToasterService,
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
  ) {}

  get isFinanceAdmin(): boolean {
    return !!this.authService.userProfile?.isFinanceAdmin;
  }

  openAddProjects(): void {
    this.openProjectModal('add');
  }

  /**
   * Open the same modal in edit mode pre-populated with this row's
   * values. On save we refresh the list (rather than splicing in the
   * updated row) so the row's position re-sorts naturally when the
   * project name was renamed.
   */
  openEditProject(row: ProjectDetailsRow): void {
    if (!this.isFinanceAdmin) return;
    if (row?.projectId == null) return;
    this.openProjectModal('edit', row);
  }

  private openProjectModal(mode: 'add' | 'edit', existing?: ProjectDetailsRow): void {
    const ref = this.dialog.open<
      AddProjectsModalComponent,
      AddProjectsModalData,
      ProjectDetailsRow | null
    >(AddProjectsModalComponent, {
      data: {
        businessSegmentOptions: this.businessSegmentOptions,
        supportingLobOptions: this.supportingLobOptions,
        autocompleteSuggestions: this.autocompleteSuggestions,
        mode,
        existing,
      },
      width: '960px',
      maxWidth: '95vw',
      // maxHeight cap — Material's GlobalPositionStrategy uses this when
      // calculating vertical centering, so leave it set even though the
      // surface CSS forces the modal to size to content (height: auto +
      // min-height: 0). Without this here the modal renders top-aligned.
      maxHeight: '92vh',
      panelClass: 'finance-add-projects-modal-panel',
      autoFocus: false,
    });
    ref.afterClosed().subscribe((saved) => {
      if (saved) {
        // Refresh from page 0 so the changed row is visible regardless of
        // current filter / sort. Default sort is project_name ASC so the
        // row lands in its natural position after any rename.
        this.page = 0;
        this.load();
      }
    });
  }

  ngOnInit(): void {
    this.subs.add(
      this.searchInput$
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe(() => {
          this.page = 0;
          this.load();
        }),
    );
    this.financeService.listReferenceEntries('business-segment').subscribe({
      next: (opts) => (this.businessSegmentOptions = opts),
      error: () => (this.businessSegmentOptions = []),
    });
    this.financeService.listReferenceEntries('supporting-lob').subscribe({
      next: (opts) => (this.supportingLobOptions = opts),
      error: () => (this.supportingLobOptions = []),
    });
    this.financeService.getProjectAutocompleteSuggestions().subscribe({
      next: (s) => (this.autocompleteSuggestions = s),
      // Silent on error — autocomplete just falls back to empty suggestion lists,
      // free typing still works.
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  backToFinanceDetails(): void {
    this.router.navigate(['/finance/details']);
  }

  backToDashboard(): void {
    this.router.navigate(['/finance']);
  }

  onSearchChange(): void {
    this.searchInput$.next();
  }

  clearSearch(): void {
    this.searchText = '';
    this.page = 0;
    this.load();
  }

  onBusinessSegmentsChange(): void {
    this.page = 0;
    this.load();
  }

  onSupportingLobsChange(): void {
    this.page = 0;
    this.load();
  }

  onPageEvent(e: PageEvent): void {
    this.page = e.pageIndex;
    this.size = e.pageSize;
    this.load();
  }

  /**
   * Add / Edit / Download actions are stubbed — the Figma flagged the
   * Download logic as a separate spec and Add / Edit need their own
   * editor screens which haven't been designed yet. Toast keeps the
   * affordance discoverable without claiming functionality.
   */
  comingSoon(label: string): void {
    this.toaster.showInfo(`${label} — coming soon`, 'Project Details');
  }

  trackByProjectId = (_: number, row: ProjectDetailsRow): number =>
    row.projectId;

  isInactive(status: string | undefined): boolean {
    return (status ?? '').toLowerCase() === 'inactive';
  }

  private buildQuery(): ProjectDetailsQuery {
    return {
      search: this.searchText.trim() || undefined,
      businessSegments: this.selectedBusinessSegments.length
        ? this.selectedBusinessSegments
        : undefined,
      supportingLobs: this.selectedSupportingLobs.length
        ? this.selectedSupportingLobs
        : undefined,
      page: this.page,
      size: this.size,
    };
  }

  private load(): void {
    this.loading = true;
    this.errorMessage = null;
    this.financeService.listProjectDetails(this.buildQuery()).subscribe({
      next: (data: ProjectDetailsPage) => {
        this.totalElements = data.totalElements;
        this.totalPages = data.totalPages;
        this.page = data.page;
        this.size = data.size;
        this.rows = data.rows ?? [];
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.totalElements = 0;
        this.totalPages = 1;
        this.errorMessage = 'Failed to load project data. Please try again.';
        this.loading = false;
      },
    });
  }
}
